import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Custom ErrorHandler that filters out expected errors.
 *
 * Filtered errors:
 * 1. Firebase Storage errors when files don't exist or can't be accessed
 *    - -100: Unknown error (often file not found)
 *    - storage/object-not-found: File doesn't exist
 *    - storage/unauthorized: User doesn't have permission
 *
 * 2. Videogular media player errors when video files don't exist
 *    - "Cannot read properties of undefined (reading 'play')"
 *    - These occur when vg-overlay-play tries to play a video that failed to load
 */
@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  // Firebase Storage error codes to filter
  private readonly filteredErrorCodes = new Set([
    -100,           // Unknown error (file not found)
    'storage/object-not-found',
    'storage/unauthorized',
    'storage/canceled',
  ]);

  // Error message patterns to filter (for third-party library errors)
  private readonly filteredErrorPatterns = [
    /Cannot read properties of undefined \(reading 'play'\)/,
    /Cannot read properties of undefined \(reading 'pause'\)/,
  ];

  handleError(error: any): void {
    // Extract the actual error from the wrapper if present
    const actualError = error?.rejection || error;

    // Check if this is a filtered error
    if (this.isFilteredError(actualError)) {
      // Silently ignore expected errors
      return;
    }

    // For all other errors, log to console as usual
    console.error('ERROR', error);
  }

  private isFilteredError(error: any): boolean {
    if (!error) return false;

    // Check Firebase error codes
    if (this.isFilteredFirebaseError(error)) {
      return true;
    }

    // Check error message patterns
    if (this.matchesFilteredPattern(error)) {
      return true;
    }

    return false;
  }

  private isFilteredFirebaseError(error: any): boolean {
    // Check error code property (Firebase errors have this)
    const code = error.code ?? error?.code;
    if (code !== undefined && this.filteredErrorCodes.has(code)) {
      return true;
    }

    // Check if error is an object with code property
    if (typeof error === 'object' && 'code' in error) {
      if (this.filteredErrorCodes.has(error.code)) {
        return true;
      }
    }

    return false;
  }

  private matchesFilteredPattern(error: any): boolean {
    // Get the error message
    const message = error?.message || (typeof error === 'string' ? error : String(error));

    // Check if the message matches any filtered pattern
    return this.filteredErrorPatterns.some(pattern => pattern.test(message));
  }
}
